<!-----------------------------------------------------------------------------
  Copyright (c) 2016 - 2021.                                                  -
  Yega Yazilim ve Bilisim Hizmetleri Dis Tic Ltd Sti (Yega Yazilim)           -
  All Rights Reserved.                                                        -
                                                                              -
  The software and data contained herein are confidential and proprietary     -
  information of Yega Yazilim ("Confidential Information").                   -
                                                                              -
  Users of this software may not disclose such Confidential Information and   -
  shall use it only in accordance with the terms of the license agreement     -
  they entered into with Yega Yazilim.                                        -
                                                                              -
  Author:  Ahmet Dogramaci                                                    -
  Date:    2021-12-14                                                         -
  ----------------------------------------------------------------------------->

<template>
  <b-modal ref="similar_products_modal"
           size="xl"
           centered
           hide-footer
           no-close-on-backdrop
           no-close-on-esc
           title="Similar Non-BTO Products Available">
    <div class="mb-3">
      <p class="text-info">
        We found similar EZBuy SKUs that might meet your requirements.
        You can add these products directly instead of configuring a custom BTO product.
      </p>
    </div>

    <div v-if="currentConfigurationDescription" class="mb-3">
      <div class="text-muted"><strong>Your Current BTO Configuration:</strong></div>
      <div class="text-muted">{{ currentConfigurationDescription }}</div>
    </div>

    <b-table striped
             hover
             :items="similarProducts"
             :fields="fields"
             show-empty
             empty-text="No similar products found">
      <template v-slot:cell(description)="data">
        <div>
          <div class="font-weight-bold">{{ data.item.description }}</div>
          <div v-if="data.item.matching_kiosk_codes" class="text-muted small mt-1">
            <strong>Matching components:</strong>
            <ul class="mb-0 mt-1">
              <li v-for="component in getMatchingComponentDescriptions(data.item.matching_kiosk_codes)" :key="component">
                {{ component }}
              </li>
            </ul>
          </div>
        </div>
      </template>
      <template v-slot:cell(actions)="data">
        <b-button variant="primary"
                  size="sm"
                  @click="addSimilarProduct(data.item)">
          Add To Quote
        </b-button>
      </template>
      <template v-slot:cell(price)="data">
        {{ formatCurrency(data.item.price) }}
      </template>
    </b-table>

    <div class="mt-3 d-flex justify-content-between">
      <b-button variant="outline-secondary" @click="proceedWithBTO">
        Continue with BTO Configuration
      </b-button>
      <b-button variant="secondary" @click="cancel">
        Cancel
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import useCurrencyFormatter from "../../compositions/use-currency-formatter";
import QuoteProduct from "../../business-objects/quote-product";

export default {
  name: "similar-products-dialog",
  props: {
    quoteProduct: {
      type: QuoteProduct,
      required: true
    }
  },
  setup(){
    const { formatCurrency } = useCurrencyFormatter();
    return {
      formatCurrency
    };
  },
  data() {
    return {
      similarProducts: [],
      fields: [
        { key: 'part_number', label: 'Part Number' },
        { key: 'description', label: 'Description' },
        { key: 'price', label: 'Price' },
        { key: 'actions', label: 'Actions' }
      ]
    };
  },
  computed: {
    currentConfigurationDescription() {
      if (!this.quoteProduct.template_version) return '';

      const selectedNodes = this.quoteProduct.template_version.selected_nodes;

      // Find the base product (starts with 'BASE')
      const baseProduct = selectedNodes.find(node => {
        const kioskCode = node.attribute_store['kiosk-code'];
        return kioskCode && kioskCode.startsWith('BASE');
      });

      // Get sorted content components (excluding base)
      const contentComponents = selectedNodes
        .filter(node =>
          node.attribute_store['item-type'] === 'content' &&
          node.attribute_store['hide-on-summary'] !== 'YES' &&
          !(node.attribute_store['kiosk-code'] || '').startsWith('BASE')
        )
        .sort((a, b) => {
          const orderA = a.attribute_store['category-order'] || 0;
          const orderB = b.attribute_store['category-order'] || 0;
          return orderA - orderB;
        })
        .map(node => (node.name || '').trim())
        .filter(name => name.length > 0);

      // Build description: "Base Product - Component1/Component2/Component3"
      const baseName = baseProduct ? baseProduct.name : '';
      const componentsString = contentComponents.join('/');

      return componentsString.length > 0
        ? `${baseName} - ${componentsString}`
        : baseName;
    }
  },
  methods: {
    show(similarProducts) {
      this.similarProducts = similarProducts;
      this.$refs.similar_products_modal.show();
    },
    hide() {
      this.$refs.similar_products_modal.hide();
    },
    getMatchingComponentDescriptions(matchingKioskCodes) {
      if (!this.quoteProduct.template_version || !matchingKioskCodes || matchingKioskCodes.length === 0) {
        return [];
      }

      const descriptions = [];
      const selectedNodes = this.quoteProduct.template_version.selected_nodes || [];
      const memoryDescriptions = [];

      matchingKioskCodes.forEach(kioskCode => {
        // Find the node with matching kiosk code
        const matchingNode = selectedNodes.find(node => {
          const nodeKioskCode = node.attribute_store ? node.attribute_store['kiosk-code'] : null;
          return nodeKioskCode === kioskCode;
        });

        const description = matchingNode ? (matchingNode.name || kioskCode) : kioskCode;

        // Check if this is a memory component (MEMO- or MEMO1- prefix)
        if (kioskCode.startsWith('MEMO-') || kioskCode.startsWith('MEMO1-')) {
          memoryDescriptions.push(description);
        } else {
          descriptions.push(description);
        }
      });

      // If we have multiple memory slots, combine them
      if (memoryDescriptions.length > 0) {
        descriptions.push(memoryDescriptions.join(' + '));
      }

      return descriptions;
    },
    addSimilarProduct(product) {
      this.$store
        .dispatch('QuoteProductsStore/create', {
          quote_part_id: this.$route.params.quote_part_id,
          cfg_template_version_id: product.cfg_template_version_id,
          cfg_item_ids: product.cfg_item_ids
        })
        .then((savedItem) => {
          this.hide();
          this.$router.push({name: 'quote_path'});
        })
        .catch((error) => {
          alert(error.response ? error.response.data : error);
        });
    },
    proceedWithBTO() {
      this.hide();
      this.$emit('proceed-with-bto');
    },
    cancel() {
      this.hide();
      this.$emit('cancelled');
    }
  }
}
</script>

<style scoped>
</style>
